import React, { useEffect, useState } from "react";
import StickyHeader from "../../../components/ModuleCommon/StickyHeader/StickyHeader";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { carrierSourcingApi } from "./api/carrierSourcingApi";
import {
  apiErrorMessage,
  mapCoverageGaps,
  mapFunnelReport,
} from "./mappers";
import { MarketTag } from "./ui";

export default function Reporting() {
  const [reporting, setReporting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [funnelRes, gapsRes] = await Promise.all([
          carrierSourcingApi.getFunnelReport(),
          carrierSourcingApi.getLaneCoverage(),
        ]);
        if (cancelled) return;
        const mapped = mapFunnelReport(funnelRes.data);
        mapped.coverageGaps = mapCoverageGaps(gapsRes.data || []);
        setReporting(mapped);
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, "Failed to load reporting"));
          setReporting(mapFunnelReport(null));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const data = reporting || mapFunnelReport(null);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white dark:bg-acs-dark-surface">
      <div className="sticky top-0 z-10 w-full bg-white shadow-md dark:bg-acs-dark-surface">
        <StickyHeader
          mode={null}
          leftLabel="Reporting"
          infoData={{
            Period: {
              label: "Period",
              value: data.periodLabel,
              readonly: true,
            },
          }}
          buttons={[
            {
              label: "Export Report",
              type: "secondary",
              onClick: () => {},
            },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-fluid-1 relative">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader message="Loading reports..." />
          </div>
        ) : null}

        {error ? (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-fluid-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {data.kpis.map((k) => (
            <div
              key={k.lab}
              className="rounded-xl border border-gray-200 bg-white p-4 dark:border-acs-dark-border dark:bg-acs-dark-surface"
            >
              <div className="mb-1.5 text-[11.5px] font-bold uppercase tracking-wide text-gray-500">
                {k.lab}
              </div>
              <div className="text-[22px] font-extrabold text-gray-900 dark:text-acs-dark-text">
                {k.val}
                {k.unit ? (
                  <span className="ml-1 text-sm font-semibold text-gray-400">
                    {k.unit}
                  </span>
                ) : null}
              </div>
              <div className="mt-1 text-[12px] text-gray-500">{k.delta}</div>
            </div>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-md border border-gray-300 bg-white dark:border-acs-dark-border">
            <div className="bg-[#1e2a74] px-fluid-2 py-fluid-1.5 text-fluid-base font-medium text-white">
              Carrier Sourcing Funnel
            </div>
            <div className="space-y-2.5 bg-[#F9FAFE] p-fluid-3">
              {data.funnel.map((f) => (
                <div key={f.label} className="flex items-center gap-3">
                  <div className="w-[140px] flex-shrink-0 text-right text-fluid-sm font-semibold text-gray-600">
                    {f.label}
                  </div>
                  <div className="h-6 flex-1 overflow-hidden rounded bg-gray-100">
                    <div
                      className="flex h-full items-center bg-[#223688] pl-2 font-mono text-dense-xs font-semibold text-white"
                      style={{ width: f.count ? f.width : "0%" }}
                    >
                      {f.count}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-gray-300 bg-white dark:border-acs-dark-border">
            <div className="bg-[#1e2a74] px-fluid-2 py-fluid-1.5 text-fluid-base font-medium text-white">
              Lane Coverage Gaps
            </div>
            <div className="bg-[#F9FAFE] p-fluid-3">
              {data.coverageGaps.length ? (
                data.coverageGaps.map((g) => (
                  <div
                    key={g.lane}
                    className="flex justify-between border-b border-gray-200 py-2.5 text-fluid-sm last:border-0"
                  >
                    <span>{g.lane}</span>
                    <MarketTag tag={g.tag} label={g.label} />
                  </div>
                ))
              ) : (
                <p className="text-fluid-sm text-gray-500">
                  No coverage gap data yet.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
